import React, { useEffect, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactPopup from '../components/ContactPopup';

const RefundPolicy = () => {
  // Popup state management
  const [popupState, setPopupState] = useState({
    isOpen: false,
    title: "Get in Touch",
    subtitle: "Connect with our admissions team to learn more about our programs",
    formType: "popup_inquiry",
    interestedProgram: ""
  });

  const openPopup = (config: {
    title?: string;
    subtitle?: string;
    formType?: string;
    interestedProgram?: string;
  } = {}) => {
    setPopupState({
      isOpen: true,
      title: config.title || "Get in Touch",
      subtitle: config.subtitle || "Connect with our admissions team to learn more about our programs",
      formType: config.formType || "popup_inquiry",
      interestedProgram: config.interestedProgram || ""
    });
  };

  const closePopup = () => {
    setPopupState(prev => ({ ...prev, isOpen: false }));
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header openPopup={openPopup} />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-lg max-w-none">
          <h1 className="text-4xl font-bold text-gray-900 mb-8">Refund Policy</h1>
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg mb-8">
            <p className="text-gray-700 leading-relaxed">
              <strong>Business identity:</strong> 1to10x is a public-facing brand name used for programs and services offered through this website. ANM ENABLERS PRIVATE LIMITED and 1to10X LLC provide such programs and services, as applicable, for their respective operations and obligations.
            </p>
          </div>
          
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>1.1.</strong> This refund policy applies exclusively to the 10X Data Analyst and AI program offered under the 1to10x brand by ANM ENABLERS PRIVATE LIMITED and 1to10X LLC for their respective operations (hereinafter referred to as "the Company").
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>1.2.</strong> The Company offers a 45 day conditional refund window to support student satisfaction and confidence in the program's value.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Eligibility Criteria</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              To be eligible for a refund, a student must meet <strong>ALL</strong> of the following criteria:
            </p>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">2.1. Time Frame:</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>The refund request must be submitted between the 30th and 45th day from the official commencement date of the course batch.</li>
                  <li>No refund requests will be entertained before the 31st day or after the 45th day.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">2.2. Course Participation:</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Attend all live online classes in their entirety during the first 30 days.</li>
                  <li>Submit all assignments issued within the first 30 days, including those with deadlines extending beyond the 30-day period.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">2.3. Attendance Requirements:</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Classes must be attended in live mode only; watching recordings does not qualify.</li>
                  <li>Students must be present for the full duration of each class.</li>
                  <li>Proper identification (first and last name) must be used when attending classes.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">2.4. Assignment Submissions:</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>All submissions must be complete, timely, and in accordance with the specified requirements.</li>
                  <li>Assignments must be submitted with a genuine intention to learn and not merely to fulfill the refund policy criteria.</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Refund Process</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>3.1.</strong> Eligible students may request a refund by writing to <a href="mailto:support@1to10x.com" className="text-blue-600 hover:text-blue-800 underline">support@1to10x.com</a> within the specified time frame.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>3.2.</strong> The Company reserves the right to take up to 60 days to process the refund, although efforts will be made to complete the process within 14 days of a valid request.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>3.3.</strong> Refunds will be issued to the original payment source.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Additional Terms and Conditions</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">4.1. Course Materials:</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Students must return any hard copy materials received, in good condition, via registered post to the Company.</li>
                  <li>Refunds will only be processed upon receipt of returned materials.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">4.2. Future Participation:</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>The Company reserves the right to refuse future course sales to individuals who have received a refund under this policy.</li>
                  <li>Students who have previously received a refund must disclose this information when attempting to purchase any future courses from the Company.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">4.3. Disclosure Rights:</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>The Company retains the right to disclose the fact that a refund was issued to future customers.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">4.4. Exceptions:</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>In cases of technical issues preventing policy compliance, exceptions may be considered if the student provides immediate notification to <a href="mailto:support@1to10x.com" className="text-blue-600 hover:text-blue-800 underline">support@1to10x.com</a> with relevant evidence.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">4.5. Non-Refundable Fees:</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>All booking fees are non-refundable, except when a student completes full enrollment and complies with this refund policy.</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Limitations</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>5.1.</strong> This refund policy is an exception to the Company's general no-refund rule and applies only to the 10X Data Analyst and AI program.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>5.2.</strong> The Company reserves the right to modify or terminate this policy at any time, with such changes applying to future enrollments.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Legal Disclaimer</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              This refund policy does not affect any statutory rights that cannot be excluded under applicable law. In the event of any conflict between this policy and applicable law, the applicable law shall prevail.
            </p>
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
              <p className="text-gray-700 leading-relaxed font-medium">
                By enrolling in the 10X Data Analyst and AI program, students acknowledge that they have read, understood, and agreed to the terms and conditions of this refund policy.
              </p>
            </div>
          </section>

          <div className="text-center mt-12 pt-8 border-t border-gray-200">
            {/* <p className="text-gray-500 text-sm">
              Last updated: {new Date().toLocaleDateString()}
            </p> */}
          </div>
        </div>
      </div>
      <Footer />
      
      <ContactPopup
        isOpen={popupState.isOpen}
        onClose={closePopup}
        title={popupState.title}
        subtitle={popupState.subtitle}
        formType={popupState.formType}
        interestedProgram={popupState.interestedProgram}
      />
    </div>
  );
};

export default RefundPolicy; 
